import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import {
  BeforeCapture,
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from "@hello-pangea/dnd";
import Text from "@/components/ui/text";
import { RevenueItemColumn, ContractServiceData } from "../types";
import { ContractRevenueItem } from "@/pages/contracts/types";
import { useTranslation } from "react-i18next";
import {
  BillingFrequency,
  ChargeType,
  ServiceItem,
  ServiceItemDetail,
  ProductV2
} from "@/pages/settings/pages/products-v2/types";
import {
  useServiceItemTypes,
  useResolveServiceItemById,
  useDeletedServiceItems
} from "@/pages/settings/pages/products-v2/hooks";
import {
  Edit,
  Delete,
  GripVertical,
  ChevronDown,
  ChevronRight,
  Reactivate,
  ContractBadge
} from "@dreamhub/designsystem/icons";
import Checkbox from "@/components/ui/checkbox";
import {
  DraftRevenueItem,
  getDraftRowStatus
} from "@/pages/contracts/components/termsDraft/types";
import { CreateRevenueItemRequest } from "@/pages/contracts/hooks";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { LICENSE_TYPE_ID, useProducts } from "@/entities/settings/hooks";
import { SECTIONS } from "@/pages/settings/pages/products-v2/consts";
import { formatDiscountCell, recordedPeriodInMonths } from "../consts";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import AddServiceDrawer from "../AddServiceDrawer";
import ServiceNotePopover from "@/components/layouts/ServiceNotePopover/ServiceNotePopover";
import AddServiceDropdown from "./AddServiceDropdown";
import { useAddBundle } from "../hooks/useAddBundle";
import ServiceEmptyState from "./ServiceEmptyState";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import {
  ContractBundle,
  isPendingRevenueItem,
  useContractBundles,
  useDeleteContractBundle,
  useDeleteRevenueItem,
  useUpdateContractBundle,
  useUpdateRevenueItemsOrder
} from "@/pages/contracts/hooks";
import {
  bundleTotals,
  groupBundleBlocks,
  linesInOrder,
  moveBlock,
  moveBundleLine
} from "../bundleRows";
import {
  isBundleDiscountRefused,
  lineWithoutBundleDiscount
} from "../bundleLines";
import TotalCell from "./TotalCell";
import { DealBundleDrawer } from "./DealBundleDrawer";
import { useCurrencyConversion } from "@/services/hooks/useCurrencyConversion";
import { cn } from "@/utils/utils";
import RevenueItemCells, {
  DraftTotalChangeIndicator,
  RevenueItemServiceFlags
} from "./RevenueItemCells";
import { PricingModel } from "@/pages/settings/pages/products-v2/types";
import { hasConfiguredPricing } from "../utils";
import { useFeatureFlag } from "@/services/features/hook";
import { TermsFieldSettings } from "@/pages/contracts/components/updateTerms/types";
import { TermsColumnChanges } from "@/pages/contracts/components/updateTerms/termsChangeMap";
import { useTermsLineEditability } from "@/pages/contracts/components/updateTerms/hooks";
import { FeatureFlags } from "@/services/features/types";

// Draft mode (contract duplication): the table edits local rows through
// these callbacks instead of the revenue-item mutations, supports bulk
// selection, and shows Modified/Deleted/New indicators vs the baseline.
export interface DraftTableProps {
  baseline: Record<string, ContractRevenueItem>;
  // Bulk selection is duplication-only; the Update terms editor edits one row.
  showSelection?: boolean;
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onDeleteItem: (id: string) => void;
  onRestoreItem: (id: string) => void;
  // Puts a modified row back to its original values.
  onRevertItem?: (id: string) => void;
  onAddItem: (request: CreateRevenueItemRequest) => void;
  onUpdateItem: (id: string, request: CreateRevenueItemRequest) => void;
}

interface RevenueItemsTableProps {
  revenueItems: ContractRevenueItem[];
  productId?: string;
  contractId?: string;
  currency?: string | null;
  onServiceAdded?: (serviceData: ContractServiceData) => void;
  showAddService?: boolean;
  confirmServiceChanges?: boolean;
  showStatusColumn?: boolean;
  draft?: DraftTableProps;
  disabled?: boolean;
  disabledTooltipText?: string;
  // Update terms only: what the server says each line may edit.
  termsFieldSettings?: TermsFieldSettings;
  lockServiceStartDate?: boolean;
  // Update terms: an amendment seeds and locks a new line's start dates from this;
  // a correction has none. A fixed term's end date may move either way.
  effectiveDate?: string;
  // Per-row, per-cell diffs from the terms preview.
  columnChangesByItem?: Record<string, TermsColumnChanges>;
  lockBillingStartDate?: boolean;
  allowServiceEndDateEdit?: boolean;
  // Hides each row's edit and delete; services then change only through Update terms.
  hideRowActions?: boolean;
  // Hides the trailing Add service button only; reorder and the drawer stay.
  hideAddService?: boolean;
  // The deal's Terms & Services also offers the product's bundles.
  offerBundles?: boolean;
  // Groups lines under their bundles without offering bundles to add (the
  // contract page); the bundle row there has no edit or delete of its own.
  groupBundles?: boolean;
}

// Quantity and Discount hold this instead of a share of the table's width:
// their widest values are a handful of characters ("100%"), so a proportional
// share leaves them far wider than anything they ever render.
const NARROW_COLUMN_WIDTH = 72;

// The `never` halves make the two forms mutually exclusive: an entry carrying
// both keys would be counted into the weighted denominator while rendering at
// its fixed width, silently narrowing every proportional column.
type ColumnHeader =
  | { key: RevenueItemColumn; weight: number; fixedWidth?: never }
  | { key: RevenueItemColumn; fixedWidth: number; weight?: never };

const COLUMN_HEADERS: readonly ColumnHeader[] = [
  { key: RevenueItemColumn.SERVICE, weight: 140 },
  { key: RevenueItemColumn.STATUS, weight: 120 },
  { key: RevenueItemColumn.PERIOD, weight: 160 },
  { key: RevenueItemColumn.QUANTITY, fixedWidth: NARROW_COLUMN_WIDTH },
  { key: RevenueItemColumn.PRICING, weight: 140 },
  { key: RevenueItemColumn.BILLING, weight: 140 },
  { key: RevenueItemColumn.DISCOUNT, fixedWidth: NARROW_COLUMN_WIDTH },
  { key: RevenueItemColumn.TOTAL, weight: 100 },
  // Holds a single icon; fits the "Note" header.
  { key: RevenueItemColumn.NOTE, fixedWidth: 56 }
];

const DRAG_COLUMN_WIDTH = 24;
const CHEVRON_COLUMN_WIDTH = 24;
const SELECT_COLUMN_WIDTH = 32;
const ACTIONS_COLUMN_WIDTH = 92;
// A modified row adds a revert button beside edit and delete.
const ACTIONS_COLUMN_WIDTH_WITH_REVERT = 136;

const sortByItemOrder = (items: ContractRevenueItem[]) =>
  [...items].sort((a, b) => {
    const orderA = a.itemOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.itemOrder ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

const RevenueItemsTable: React.FC<RevenueItemsTableProps> = ({
  revenueItems,
  productId,
  contractId,
  currency: rawCurrency,
  onServiceAdded,
  showAddService = true,
  confirmServiceChanges = false,
  showStatusColumn = true,
  draft,
  disabled = false,
  disabledTooltipText,
  termsFieldSettings,
  lockServiceStartDate = false,
  effectiveDate,
  columnChangesByItem,
  lockBillingStartDate = false,
  allowServiceEndDateEdit = false,
  hideRowActions = false,
  hideAddService = false,
  offerBundles = false,
  groupBundles = false
}) => {
  // `??` (not a default param) so a null currency — e.g. a contract with no
  // currency set — falls back too; a default param only fires on undefined.
  const currency = rawCurrency ?? "USD";
  const { enabled: periodDrilldownEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8968_PERIOD_DRILLDOWN
  );
  // Draft mode (contract duplication) ignores elapsed periods entirely.
  const showPeriodDrilldown = periodDrilldownEnabled && !draft;
  const { enabled: bundlesEnabled } = useFeatureFlag(
    FeatureFlags.CRM_11089_PRODUCT_BUNDLES
  );
  // The deal's and the contract's Terms & Services group lines under their
  // bundles; the duplicate draft and Update terms don't.
  const shouldGroupBundles =
    (offerBundles || groupBundles) && !draft && bundlesEnabled;
  const hasBundleLines =
    shouldGroupBundles && revenueItems.some((item) => !!item.contractBundleId);
  // A bundle row expands from the same column as an item's elapsed periods.
  const showChevronColumn = showPeriodDrilldown || hasBundleLines;
  const showDraftSelection = !!draft && draft.showSelection !== false;
  const actionsColumnWidth = draft?.onRevertItem
    ? ACTIONS_COLUMN_WIDTH_WITH_REVERT
    : ACTIONS_COLUMN_WIDTH;
  const resolveLineEditability = useTermsLineEditability(termsFieldSettings);

  const { enabled: showNoteColumn } = useFeatureFlag(
    FeatureFlags.CRM_10582_SERVICE_NOTES
  );

  const visibleColumns = useMemo(
    () =>
      COLUMN_HEADERS.filter(
        (col) =>
          (showStatusColumn || col.key !== RevenueItemColumn.STATUS) &&
          (showNoteColumn || col.key !== RevenueItemColumn.NOTE)
      ),
    [showStatusColumn, showNoteColumn]
  );

  const dataColumnWidths = useMemo(() => {
    const weightTotal = visibleColumns.reduce(
      (sum, col) => sum + (col.weight ?? 0),
      0
    );
    const leadingColumnWidth = draft
      ? showDraftSelection
        ? SELECT_COLUMN_WIDTH
        : 0
      : showAddService
        ? DRAG_COLUMN_WIDTH
        : 0;
    const fixedTotal =
      leadingColumnWidth +
      (showChevronColumn ? CHEVRON_COLUMN_WIDTH : 0) +
      actionsColumnWidth +
      visibleColumns.reduce(
        (sum, col) => sum + (col.fixedWidth ?? 0),
        0
      );
    return visibleColumns.map((col) =>
      col.fixedWidth !== undefined
        ? `${col.fixedWidth}px`
        : `calc((100% - ${fixedTotal}px) * ${(col.weight ?? 0) / weightTotal})`
    );
  }, [
    visibleColumns,
    showAddService,
    draft,
    showDraftSelection,
    showChevronColumn,
    actionsColumnWidth
  ]);
  const { t } = useTranslation();
  const { data: serviceItemTypes = [] } = useServiceItemTypes();
  const resolveServiceItemById = useResolveServiceItemById();
  const {
    data: productsData,
    isLoading: isProductsLoading,
    isError: isProductsError
  } = useProducts();
  // A failed load with nothing cached leaves no catalog to edit against.
  const isCatalogReady =
    !isProductsLoading && !(isProductsError && !productsData);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedService, setSelectedService] =
    useState<ServiceItemDetail | null>(null);
  const [editingRevenueItem, setEditingRevenueItem] =
    useState<ContractRevenueItem | null>(null);
  const [pendingEditItem, setPendingEditItem] =
    useState<ContractRevenueItem | null>(null);
  const [pendingDeleteItem, setPendingDeleteItem] =
    useState<ContractRevenueItem | null>(null);
  const [orderedItems, setOrderedItems] = useState<ContractRevenueItem[]>(() =>
    sortByItemOrder(revenueItems)
  );
  const [expandedPeriodItemIds, setExpandedPeriodItemIds] = useState<string[]>(
    []
  );
  const { data: contractBundles, isError: hasBundlesError } =
    useContractBundles(contractId, {
      enabled: hasBundleLines
    });
  const blocks = useMemo(
    () =>
      groupBundleBlocks(
        orderedItems,
        hasBundleLines ? (contractBundles?.bundles ?? []) : []
      ),
    [orderedItems, hasBundleLines, contractBundles]
  );
  const hasBundles = blocks.some((block) => block.kind === "bundle");
  // Until every bundle line's bundle has loaded, such a line shows on its own,
  // and moving it could split its bundle, which the order save rejects; a
  // failed load says so.
  const areBundlesUnavailable =
    hasBundleLines &&
    (!contractBundles ||
      orderedItems.some(
        (item) =>
          !!item.contractBundleId &&
          !contractBundles.bundles.some(
            ({ id }) => id === item.contractBundleId
          )
      ));
  const hasBundlesLoadFailed = hasBundlesError && areBundlesUnavailable;
  // Said once when the load fails, not on every render while it stays failed.
  const wasBundlesLoadFailedRef = useRef(false);
  useEffect(() => {
    if (hasBundlesLoadFailed && !wasBundlesLoadFailedRef.current) {
      toast.error(t("contractTerms.bundle.loadError"));
    }
    wasBundlesLoadFailedRef.current = hasBundlesLoadFailed;
  }, [hasBundlesLoadFailed, t]);
  const [expandedBundleIds, setExpandedBundleIds] = useState<string[]>([]);
  // While a bundle or a standalone line is dragged, bundles show collapsed so
  // whole blocks move; a bundle's line moves only among its own lines.
  const [isBlockDrag, setIsBlockDrag] = useState(false);
  const [pendingDeleteBundle, setPendingDeleteBundle] =
    useState<ContractBundle | null>(null);
  const [editingBundle, setEditingBundle] = useState<{
    bundle: ContractBundle;
    lines: ContractRevenueItem[];
  } | null>(null);
  // A bundle deleted while its drawer is open, from one of its lines, closes it.
  const isEditingBundleGone =
    !!editingBundle &&
    !!contractBundles &&
    !blocks.some(
      (block) =>
        block.kind === "bundle" && block.bundle.id === editingBundle.bundle.id
    );
  useEffect(() => {
    if (isEditingBundleGone) setEditingBundle(null);
  }, [isEditingBundleGone]);
  // The bundle as the drawer takes it, kept stable while the drawer is open.
  const editingBundleDetails = useMemo(
    () =>
      editingBundle && {
        ...editingBundle.bundle,
        items: editingBundle.bundle.items ?? []
      },
    [editingBundle]
  );
  const deleteContractBundleMutation = useDeleteContractBundle();
  const updateContractBundleMutation = useUpdateContractBundle();
  // Deleting a bundle's only line deletes the bundle, so it asks as a bundle.
  const isLastBundleLine = (item: ContractRevenueItem) =>
    blocks.some(
      (block) =>
        block.kind === "bundle" &&
        block.bundle.id === item.contractBundleId &&
        block.lines.length === 1
    );
  // A bundle's lines follow its row while it's expanded.
  const rows = blocks.flatMap((block) =>
    block.kind === "item"
      ? [{ kind: "item" as const, item: block.item, inBundle: false }]
      : [
          block,
          ...(!isBlockDrag && expandedBundleIds.includes(block.bundle.id)
            ? block.lines.map((item) => ({
                kind: "item" as const,
                item,
                inBundle: true
              }))
            : [])
        ]
  );
  const deleteRevenueItemMutation = useDeleteRevenueItem();
  const updateRevenueItemsOrderMutation = useUpdateRevenueItemsOrder();
  const updateRevenueItemsOrderMutationRef = useRef(
    updateRevenueItemsOrderMutation
  );
  useEffect(() => {
    updateRevenueItemsOrderMutationRef.current =
      updateRevenueItemsOrderMutation;
  }, [updateRevenueItemsOrderMutation]);

  const {
    accountCurrencySymbol,
    recordCurrencySignOverride,
    showConversion,
    rate
  } =
    // The raw prop, not the display fallback above: the hook's guard is what
    // suppresses a conversion for a contract whose currency is simply unset.
    useCurrencyConversion(rawCurrency);

  useEffect(() => {
    setOrderedItems(sortByItemOrder(revenueItems));
    // Drop ids of items that left the list or lost their periods (auto-renewal
    // switched off); a stale id would keep drag-reorder disabled with no
    // chevron left to collapse it.
    setExpandedPeriodItemIds((current) =>
      current.filter((id) =>
        revenueItems.some(
          (item) => item.id === id && (item.previousPeriods?.length ?? 0) > 0
        )
      )
    );
  }, [revenueItems]);

  useLayoutEffect(() => {
    // Close any in-flight edit/add/delete UI when the financials lock lands
    // mid-session — the open drawer and confirmation modals would otherwise
    // let the workflow complete after the controls were disabled.
    if (disabled) {
      // Invalidate any edit lookup in flight — if it resolves after the
      // table is re-enabled, disabledRef.current would no longer catch it,
      // and this is the only other signal it has to go stale by.
      editRequestIdRef.current += 1;
      setIsDrawerOpen(false);
      setSelectedService(null);
      setEditingRevenueItem(null);
      setPendingEditItem(null);
      setPendingDeleteItem(null);
      setPendingDeleteBundle(null);
      setEditingBundle(null);
    }
  }, [disabled]);

  // Mirrors `disabled` for the async service-item lookup in openEditDrawer —
  // that lookup can straddle a disabled transition, and a stale closure over
  // the prop would reopen the drawer the effect above just closed.
  const disabledRef = useRef(disabled);
  useLayoutEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  // Identifies the current in-flight (or most recent) drawer-open request —
  // bumped on every new edit or add open, and on close, so a slower
  // superseded edit lookup can tell it's stale and drop its result instead
  // of overwriting the row the drawer is now showing.
  const editRequestIdRef = useRef(0);
  useLayoutEffect(() => {
    // A pending lookup is scoped to the product/contract it was issued
    // under — if either changes before it resolves, its result belongs to
    // a table the user is no longer looking at.
    editRequestIdRef.current += 1;
  }, [productId, contractId]);

  const selectedProduct = useMemo(() => {
    if (!productId || !productsData?.products) return null;
    return (
      productsData.products.find((p: ProductV2) => p.id === productId) || null
    );
  }, [productId, productsData]);

  const availableServices = useMemo(() => {
    if (!selectedProduct) return [];
    return (selectedProduct?.serviceItems ?? [])
      .map((item: ServiceItem) => ({
        ...item,
        productId: item.productId || productId
      }))
      .filter(hasConfiguredPricing) as ServiceItem[];
  }, [productId, selectedProduct]);

  // Unlike availableServices, intentionally unfiltered by product or pricing
  // config — existing revenue items must still resolve their service item
  // even if it's since lost pricing or moved products.
  const allServices = useMemo(
    () =>
      (productsData?.products ?? []).flatMap((product: ProductV2) =>
        (product.serviceItems ?? []).map((item: ServiceItem) => ({
          ...item,
          productId: item.productId || product.id
        }))
      ) as ServiceItem[],
    [productsData]
  );

  // Rows whose service item was deleted from the catalog still render from it
  // (type, charge type, pricing), so resolve those items by id.
  const deletedServiceItemIds = useMemo(() => {
    if (!productsData) return [];
    const catalogIds = new Set(allServices.map((service) => service.id));
    return Array.from(
      new Set(
        revenueItems
          .map((item) => item.serviceItemId)
          .filter((id): id is string => !!id && !catalogIds.has(id))
      )
    );
  }, [productsData, allServices, revenueItems]);
  const deletedServices = useDeletedServiceItems(
    productId,
    deletedServiceItemIds
  );

  const {
    bundles,
    onSelectBundle,
    drawer: bundleDrawer
  } = useAddBundle({
    // A draft has no contract to add a bundle to yet.
    offerBundles: offerBundles && !draft,
    productId,
    contractId,
    services: availableServices,
    serviceItemTypes,
    currency: rawCurrency,
    disabled
  });

  const handleServiceSelect = (service: ServiceItem) => {
    if (disabled) return;
    // Invalidate any edit-drawer lookup still in flight — otherwise it can
    // land after this add and overwrite the drawer back into edit mode.
    editRequestIdRef.current += 1;
    setSelectedService(service);
    setIsDrawerOpen(true);
  };

  const handleAddToContract = (serviceData: ContractServiceData) => {
    if (disabled) return;
    onServiceAdded?.(serviceData);
    setIsDrawerOpen(false);
    setSelectedService(null);
    setEditingRevenueItem(null);
  };

  const handleCloseDrawer = () => {
    editRequestIdRef.current += 1;
    setIsDrawerOpen(false);
    setSelectedService(null);
    setEditingRevenueItem(null);
  };

  const openEditDrawer = async (revenueItem: ContractRevenueItem) => {
    if (disabled || isProductsLoading) return;
    // Invalidates any lookup already in flight for a different row — its
    // result must not land after this one, whichever resolves first.
    editRequestIdRef.current += 1;
    const requestId = editRequestIdRef.current;
    if (isProductsError && !productsData) {
      // The catalog itself failed to load with no cached data to fall back
      // on — a lookup miss here says nothing about whether this row's
      // service item still exists, so don't claim it's gone; report the
      // fetch failure instead. A background refetch error with cached data
      // still present falls through to the normal resolution below.
      toast.error(t("deals.commercials.updateError"));
      return;
    }
    const serviceItem = allServices.find(
      (service) => service.id === revenueItem.serviceItemId
    );
    if (serviceItem) {
      setSelectedService(serviceItem);
      setEditingRevenueItem(revenueItem);
      setIsDrawerOpen(true);
      return;
    }
    // Not in the bulk catalog — the bulk fetch never returns soft-deleted
    // service items, which is the common way a row lands here. Fall back to
    // a direct lookup by the item's id, which resolves it even when it no
    // longer belongs to this table's product.
    if (productId && revenueItem.serviceItemId) {
      let resolvedServiceItem: ServiceItemDetail | null = null;
      try {
        resolvedServiceItem = await resolveServiceItemById(
          productId,
          revenueItem.serviceItemId
        );
      } catch {
        // Distinct from a confirmed-missing item: don't claim it's gone
        // when the lookup itself failed.
        if (!disabledRef.current && requestId === editRequestIdRef.current) {
          toast.error(t("deals.commercials.updateError"));
        }
        return;
      }
      // The lock can land while the lookup is in flight; the effect above
      // already closed the drawer for it, so don't reopen it underneath.
      // Likewise, another row's edit (or a close) can start and finish while
      // this lookup is still in flight — a stale result must not overwrite
      // whatever the table is now showing.
      if (disabledRef.current || requestId !== editRequestIdRef.current)
        return;
      if (resolvedServiceItem) {
        setSelectedService(resolvedServiceItem);
        setEditingRevenueItem(revenueItem);
        setIsDrawerOpen(true);
        return;
      }
    }
    if (!disabledRef.current && requestId === editRequestIdRef.current) {
      toast.error(t("deals.commercials.serviceItemUnavailable"));
    }
  };

  const performDelete = (revenueItem: ContractRevenueItem) => {
    if (disabled || !contractId || !revenueItem.id) return;

    // Invalidate any edit-drawer lookup in flight — otherwise it can resolve
    // after this delete and reopen the drawer for a row that no longer exists.
    editRequestIdRef.current += 1;

    deleteRevenueItemMutation.mutate({
      pathParams: {
        contractId,
        revenueItemId: revenueItem.id
      }
    });
  };

  // Removes the bundle and all of its lines.
  const performBundleDelete = (bundle: ContractBundle) => {
    if (disabled || !contractId) return;
    editRequestIdRef.current += 1;
    deleteContractBundleMutation.mutate(
      { pathParams: { bundleId: bundle.id, contractId } },
      { onError: () => toast.error(t("contractTerms.bundle.deleteError")) }
    );
  };

  const handleEditRevenueItem = (revenueItem: ContractRevenueItem) => {
    if (disabled) return;
    if (draft) {
      openEditDrawer(revenueItem);
      return;
    }
    if (confirmServiceChanges) {
      // Invalidate any lookup already in flight now, not only once the user
      // confirms — an uninvalidated stale lookup can still resolve and
      // reopen the drawer even if this confirmation is cancelled.
      editRequestIdRef.current += 1;
      setPendingEditItem(revenueItem);
      return;
    }
    openEditDrawer(revenueItem);
  };

  const handleDeleteRevenueItem = (revenueItem: ContractRevenueItem) => {
    if (draft) {
      editRequestIdRef.current += 1;
      draft.onDeleteItem(revenueItem.id);
      return;
    }
    setPendingDeleteItem(revenueItem);
  };

  const selectableIds = useMemo(
    () =>
      draft
        ? orderedItems
            .filter((item) => !(item as DraftRevenueItem).draftDeleted)
            .map((item) => item.id)
        : [],
    [draft, orderedItems]
  );
  const allSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => draft?.selectedIds.includes(id));
  const someSelected = (draft?.selectedIds.length ?? 0) > 0 && !allSelected;
  const hasPendingItems = orderedItems.some(isPendingRevenueItem);

  // A drag starting on anything but a bundle's line moves whole blocks, so the
  // bundles collapse before the rows are measured.
  const handleBeforeCapture = useCallback(
    ({ draggableId }: BeforeCapture) => {
      if (!hasBundles) return;
      const isBundleLine = rows.some(
        (row) =>
          row.kind === "item" && row.inBundle && row.item.id === draggableId
      );
      if (!isBundleLine) flushSync(() => setIsBlockDrag(true));
    },
    [hasBundles, rows]
  );

  // Saves the table's new order; a bundle's lines always stay together.
  const saveOrder = useCallback(
    (items: ContractRevenueItem[]) => {
      if (!contractId) return;
      setOrderedItems(items);
      const order: Record<number, string> = {};
      items.forEach((item, index) => {
        order[index] = item.id;
      });
      updateRevenueItemsOrderMutationRef.current.mutate({
        pathParams: { contractId },
        data: { items: order }
      });
    },
    [contractId]
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      const { destination, source } = result;
      const wasBlockDrag = isBlockDrag;
      setIsBlockDrag(false);

      if (disabled) return;
      if (!destination) return;
      if (destination.index === source.index) return;
      if (!contractId || draft) return;
      if (hasPendingItems || areBundlesUnavailable) return;

      if (hasBundles) {
        if (wasBlockDrag) {
          saveOrder(
            linesInOrder(moveBlock(blocks, source.index, destination.index))
          );
          return;
        }
        // A bundle's line lands only among its own lines.
        const dragged = rows[source.index];
        if (dragged?.kind !== "item" || !dragged.item.contractBundleId) return;
        const bundleId = dragged.item.contractBundleId;
        const firstLine = rows.findIndex(
          (row) =>
            row.kind === "item" &&
            row.inBundle &&
            row.item.contractBundleId === bundleId
        );
        const lineCount = rows.filter(
          (row) =>
            row.kind === "item" &&
            row.inBundle &&
            row.item.contractBundleId === bundleId
        ).length;
        if (
          destination.index < firstLine ||
          destination.index >= firstLine + lineCount
        ) {
          return;
        }
        saveOrder(
          linesInOrder(
            moveBundleLine(
              blocks,
              bundleId,
              source.index - firstLine,
              destination.index - firstLine
            )
          )
        );
        return;
      }

      const reordered = [...orderedItems];
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);

      saveOrder(
        reordered.map((item, index) => ({
          ...item,
          itemOrder: index
        }))
      );
    },
    [
      orderedItems,
      contractId,
      draft,
      disabled,
      hasPendingItems,
      areBundlesUnavailable,
      hasBundles,
      isBlockDrag,
      blocks,
      rows,
      saveOrder
    ]
  );

  const serviceOptions: DetailedViewOptions = useMemo(() => {
    const options: DetailedViewOptions = {};

    availableServices.forEach((service) => {
      const serviceType = serviceItemTypes.find(
        (type) => type.id === service.typeId
      );
      options[service.id] = {
        action: () => handleServiceSelect(service),
        customText: service.name,
        section: service.typeId === LICENSE_TYPE_ID ? "1" : "2",
        secondaryText: serviceType?.name || String(service.typeId)
      };
    });

    return options;
    // handleServiceSelect reads `disabled`; recompute the actions when the
    // lock flips so a re-enabled menu doesn't keep no-op actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableServices, serviceItemTypes, disabled]);

  const sections = SECTIONS.map((section) => ({
    id: section.id.toString(),
    title: t(section.name)
  }));

  const getServiceItem = (item: ContractRevenueItem) => {
    return (
      allServices.find((service) => service.id === item.serviceItemId) ??
      deletedServices.find((service) => service.id === item.serviceItemId)
    );
  };

  const getServiceType = (item: ContractRevenueItem) => {
    const serviceItem = getServiceItem(item);
    return serviceItem
      ? serviceItemTypes.find((type) => type.id === serviceItem.typeId)
      : null;
  };

  const toggleBundle = (bundleId: string) =>
    setExpandedBundleIds((current) =>
      current.includes(bundleId)
        ? current.filter((id) => id !== bundleId)
        : [...current, bundleId]
    );

  const togglePeriods = (itemId: string) =>
    setExpandedPeriodItemIds((current) =>
      current.includes(itemId)
        ? current.filter((id) => id !== itemId)
        : [...current, itemId]
    );

  // Flags derived from a row's service item; elapsed periods share them with
  // their current-period row.
  const getServiceFlags = (
    item: ContractRevenueItem
  ): RevenueItemServiceFlags => {
    const serviceItem = getServiceItem(item);
    const isFlexiblePricing =
      serviceItem?.pricingModel === PricingModel.FLEXIBLE;
    const isOneTime = serviceItem?.chargeType === ChargeType.ONE_TIME;
    const pricing = serviceItem?.pricing?.find(
      (row) => row.id === item.serviceItemPricingId
    );
    return {
      isFlexiblePricing,
      isPricingPerPeriod:
        isFlexiblePricing ||
        serviceItem?.pricingModel === PricingModel.FLAT_RATE,
      periodInMonths: recordedPeriodInMonths(
        item,
        pricing?.serviceItemPeriods,
        isOneTime
      ),
      isOneTime,
      // The row's own bills-once signal is a fallback so a one-time line never
      // shows auto-renew when the service-item lookup misses.
      isOneTimeLine:
        isOneTime || item.customBillingFrequency === BillingFrequency.ONCE,
      serviceTypeName: getServiceType(item)?.name
    };
  };

  // A bundle's row: its name, how many lines it has and their total. It has
  // no period, since its lines can run for different ones.
  const renderBundleRow = (
    bundle: ContractBundle,
    lines: ContractRevenueItem[],
    index: number,
    isDragDisabled: boolean
  ) => {
    const isExpanded = expandedBundleIds.includes(bundle.id);
    return (
      <Draggable
        key={`bundle:${bundle.id}`}
        draggableId={`bundle:${bundle.id}`}
        index={index}
        isDragDisabled={isDragDisabled}
      >
        {(provided, snapshot) => (
          <tr
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={cn(
              "group border-b border-border-border1",
              snapshot.isDragging &&
                "bg-surface-elevation2 shadow-md rounded-lg"
            )}
            data-test={`revenue-item-bundle-row-${bundle.id}`}
          >
            {showAddService && (
              <td className="w-6 px-1" {...provided.dragHandleProps}>
                {isDragDisabled ? null : (
                  <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                    <IconContainer color="tertiary">
                      <GripVertical />
                    </IconContainer>
                  </div>
                )}
              </td>
            )}
            <td className="px-1">
              <IconButton
                variant="iconNoBg"
                size="sm"
                iconColor="primary"
                onClick={() => toggleBundle(bundle.id)}
                aria-expanded={isExpanded}
                aria-label={t("contractTerms.bundle.toggle", {
                  name: bundle.name
                })}
                data-test={`revenue-item-bundle-toggle-${bundle.id}`}
              >
                {isExpanded ? <ChevronDown /> : <ChevronRight />}
              </IconButton>
            </td>
            {visibleColumns.map((col) =>
              col.key === RevenueItemColumn.SERVICE ? (
                <td key={col.key} className="px-2.5 py-4">
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <Text variant="label" className="truncate">
                      {bundle.name}
                    </Text>
                    <Text variant="smBodySecondary">
                      {t("contractTerms.bundle.serviceCount", {
                        count: lines.length
                      })}
                    </Text>
                  </div>
                </td>
              ) : col.key === RevenueItemColumn.DISCOUNT ? (
                <td
                  key={col.key}
                  className="px-2.5 py-4"
                  data-test={`revenue-item-bundle-discount-${bundle.id}`}
                >
                  <Text variant="body">
                    {formatDiscountCell(
                      bundle.discountAmount,
                      bundle.isDiscountPercentage,
                      currency,
                      recordCurrencySignOverride
                    )}
                  </Text>
                </td>
              ) : col.key === RevenueItemColumn.TOTAL ? (
                <td
                  key={col.key}
                  className="px-2.5 py-4"
                  data-test={`revenue-item-bundle-total-${bundle.id}`}
                >
                  <TotalCell
                    // The lines' stored totals have the bundle discount taken
                    // off, so they add up to the discounted total.
                    item={{
                      ...bundleTotals(lines),
                      discountAmount: bundle.discountAmount ?? 0,
                      isDiscountPercentage: bundle.isDiscountPercentage ?? null
                    }}
                    totalsBeforeDiscount={bundleTotals(
                      lines.map(lineWithoutBundleDiscount)
                    )}
                    currency={currency}
                    showConversion={showConversion}
                    rate={rate}
                    accountCurrencySymbol={accountCurrencySymbol}
                    currencySignOverride={recordCurrencySignOverride}
                  />
                </td>
              ) : (
                <td key={col.key} />
              )
            )}
            <td className="px-2.5 py-4">
              {/* Only where bundles are offered; elsewhere a bundle changes
                  through its lines or Update terms. */}
              {hideRowActions || !offerBundles ? null : (
                <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconButton
                    variant="icon"
                    size="md"
                    onClick={() => setEditingBundle({ bundle, lines })}
                    iconColor="primary"
                    // The drawer sets up its services when it opens, so it
                    // needs the catalog, as editing a service does.
                    disabled={disabled || !isCatalogReady}
                    tooltipText={
                      disabled
                        ? disabledTooltipText
                        : t("contractTerms.table.edit")
                    }
                    data-test={`revenue-item-bundle-edit-${bundle.id}`}
                    iconClassName="[&_path]:[vector-effect:none]"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    variant="icon"
                    size="md"
                    destructive
                    onClick={() => setPendingDeleteBundle(bundle)}
                    disabled={disabled}
                    tooltipText={
                      disabled
                        ? disabledTooltipText
                        : t("contractTerms.table.delete")
                    }
                    data-test={`revenue-item-bundle-delete-${bundle.id}`}
                  >
                    <Delete />
                  </IconButton>
                </div>
              )}
            </td>
          </tr>
        )}
      </Draggable>
    );
  };

  const showDraftEmptyState = !!draft && orderedItems.length === 0;

  return (
    <>
      <div className="flex flex-col">
        {/* Mirrors ContractTermsV2's EmptyState so the duplicate reads the same
            as the contract; that component can't be reused directly because it
            creates a contract on service select. */}
        {showDraftEmptyState ? (
          <div
            className="flex flex-col items-center justify-center h-100 rounded-lg p-6 gap-6"
            data-test="revenue-items-empty-state"
          >
            <ContractBadge />
            <div className="flex flex-col items-center justify-center">
              <Text variant="body" className="mb-2">
                {t("contractTerms.noServicesYet")}
              </Text>
              <Text variant="bodySecondary">
                {t("contractTerms.noServicesDescription")}
              </Text>
            </div>
            {showAddService && productId ? (
              <AddServiceDropdown
                serviceOptions={serviceOptions}
                sections={sections}
                variant="primary"
                emptyStateContent={<ServiceEmptyState />}
                disabled={disabled}
                disabledTooltipText={disabledTooltipText}
              />
            ) : null}
            {showAddService && !productId ? (
              <Text variant="bodyTertiary">
                {t("contractTerms.selectProductFirst")}
              </Text>
            ) : null}
          </div>
        ) : null}
        <table
          className={cn(
            "w-full table-fixed select-none",
            showDraftEmptyState && "hidden"
          )}
        >
          <colgroup>
            {draft
              ? showDraftSelection && (
                  <col style={{ width: `${SELECT_COLUMN_WIDTH}px` }} />
                )
              : showAddService && (
                  <col style={{ width: `${DRAG_COLUMN_WIDTH}px` }} />
                )}
            {showChevronColumn ? (
              <col style={{ width: `${CHEVRON_COLUMN_WIDTH}px` }} />
            ) : null}
            {dataColumnWidths.map((width, index) => (
              <col key={visibleColumns[index].key} style={{ width }} />
            ))}
            <col style={{ width: `${actionsColumnWidth}px` }} />
          </colgroup>
          <thead>
            <tr className="border-b border-border-border1">
              {draft
                ? showDraftSelection && (
                    <th scope="col" className="px-2 py-2">
                      <Checkbox
                        checked={allSelected}
                        variant={someSelected ? "partial" : "default"}
                        onCheckedChange={draft.onToggleSelectAll}
                        aria-label={t("contractTerms.table.selectAll")}
                        data-test="revenue-items-select-all"
                      />
                    </th>
                  )
                : showAddService && (
                    <th
                      scope="col"
                      aria-label={t("contractTerms.table.reorder")}
                    />
                  )}
              {showChevronColumn ? (
                <th
                  scope="col"
                  // The column also expands bundles once the deal has one.
                  aria-label={t(
                    hasBundleLines
                      ? "contractTerms.table.expand"
                      : "contractTerms.table.expandPeriods"
                  )}
                />
              ) : null}
              {visibleColumns.map((col) => (
                <th key={col.key} scope="col" className="px-2.5 py-2 text-left">
                  <Text variant="smBodySecondary">
                    {t(`contractTerms.table.${col.key}`)}
                  </Text>
                </th>
              ))}
              <th scope="col" aria-label={t("contractTerms.table.actions")} />
            </tr>
          </thead>
          <DragDropContext
            onBeforeCapture={handleBeforeCapture}
            onDragEnd={handleDragEnd}
          >
            <Droppable droppableId="revenue-items-table">
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {rows.map((row, index) => {
                    if (row.kind === "bundle") {
                      return renderBundleRow(
                        row.bundle,
                        row.lines,
                        index,
                        !showAddService ||
                          disabled ||
                          hasPendingItems ||
                          areBundlesUnavailable ||
                          expandedPeriodItemIds.length > 0
                      );
                    }
                    const { item, inBundle } = row;
                    const serviceFlags = getServiceFlags(item);
                    const previousPeriods = showPeriodDrilldown
                      ? (item.previousPeriods ?? [])
                      : [];
                    const isExpanded = expandedPeriodItemIds.includes(item.id);
                    // @hello-pangea/dnd requires Draggable children to be direct
                    // siblings inside the Droppable — expired-period rows are
                    // plain <tr> siblings, so dragging must be disabled whenever
                    // any are expanded. Shared with the handle below so the grip
                    // is hidden whenever dragging is actually disabled.
                    // A row still being saved has no server id to act on yet,
                    // and the order can't be saved until every row has one.
                    const isPending = isPendingRevenueItem(item);
                    const isDragDisabled =
                      !showAddService ||
                      disabled ||
                      hasPendingItems ||
                      areBundlesUnavailable ||
                      !!draft ||
                      expandedPeriodItemIds.length > 0;
                    return (
                      <React.Fragment key={item.id}>
                        <Draggable
                          draggableId={item.id}
                          index={index}
                          isDragDisabled={isDragDisabled}
                        >
                          {(provided, snapshot) => {
                            const draftStatus = draft
                              ? getDraftRowStatus(
                                  item as DraftRevenueItem,
                                  draft.baseline
                                )
                              : null;
                            const isDraftDeleted = draftStatus === "deleted";
                            return (
                              <tr
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={cn(
                                  "group border-b border-border-border1",
                                  inBundle && "bg-surface-recessed",
                                  snapshot.isDragging &&
                                    "bg-surface-elevation2 shadow-md rounded-lg",
                                  isDraftDeleted &&
                                    "[&>td:not(:first-child):not(:last-child)]:opacity-50"
                                )}
                              >
                                {draft
                                  ? showDraftSelection && (
                                      <td className="px-2 py-2">
                                        <Checkbox
                                          checked={draft.selectedIds.includes(
                                            item.id
                                          )}
                                          disabled={isDraftDeleted}
                                          onCheckedChange={() =>
                                            draft.onToggleSelect(item.id)
                                          }
                                          data-test={`revenue-item-select-${item.id}`}
                                        />
                                      </td>
                                    )
                                  : showAddService && (
                                      <td
                                        className="w-6 px-1"
                                        {...provided.dragHandleProps}
                                      >
                                        {isDragDisabled ? null : (
                                          <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-grab">
                                            <IconContainer color="tertiary">
                                              <GripVertical />
                                            </IconContainer>
                                          </div>
                                        )}
                                      </td>
                                    )}
                                {showChevronColumn ? (
                                  <td className="px-1">
                                    {previousPeriods.length > 0 ? (
                                      <IconButton
                                        variant="iconNoBg"
                                        size="sm"
                                        iconColor="primary"
                                        onClick={() => togglePeriods(item.id)}
                                        aria-label={t(
                                          isExpanded
                                            ? "contractTerms.table.collapsePeriods"
                                            : "contractTerms.table.expandPeriods"
                                        )}
                                        data-test={`revenue-item-toggle-periods-${item.id}`}
                                      >
                                        {isExpanded ? (
                                          <ChevronDown />
                                        ) : (
                                          <ChevronRight />
                                        )}
                                      </IconButton>
                                    ) : null}
                                  </td>
                                ) : null}
                                <RevenueItemCells
                                  // A bundle's line shows its own price and
                                  // total; the discount shows on the bundle.
                                  item={
                                    inBundle
                                      ? lineWithoutBundleDiscount(item)
                                      : item
                                  }
                                  {...serviceFlags}
                                  currency={currency}
                                  showConversion={showConversion}
                                  rate={rate}
                                  accountCurrencySymbol={accountCurrencySymbol}
                                  currencySignOverride={recordCurrencySignOverride}
                                  showStatusColumn={showStatusColumn}
                                  isDraft={!!draft}
                                  draftStatus={draftStatus}
                                  columnChanges={columnChangesByItem?.[item.id]}
                                  effectiveDate={effectiveDate}
                                  isSaving={isPending}
                                  showNoteColumn={showNoteColumn}
                                  noteCell={
                                    <ServiceNotePopover
                                      contractId={contractId ?? ""}
                                      revenueItemId={item.id}
                                      note={item.note}
                                      noteUpdatedBy={item.noteUpdatedBy}
                                      // Draft rows are local until the draft is
                                      // saved; their notes change in the drawer.
                                      readOnly={!!draft || !contractId}
                                      editDisabled={disabled || isPending}
                                      editDisabledTooltipText={
                                        disabled
                                          ? disabledTooltipText
                                          : t("contractTerms.table.saving")
                                      }
                                    />
                                  }
                                />
                                <td className="px-2.5 py-4">
                                  <div className="flex items-center justify-end gap-1">
                                    {draft ? (
                                      <div className="mr-auto">
                                        <DraftTotalChangeIndicator
                                          status={draftStatus}
                                          total={item.totalLocal}
                                          baselineTotal={
                                            draft.baseline[item.id]?.totalLocal
                                          }
                                        />
                                      </div>
                                    ) : null}
                                    {isDraftDeleted ? (
                                      <>
                                        <IconButton
                                          variant="icon"
                                          size="sm"
                                          onClick={() =>
                                            draft?.onRestoreItem(item.id)
                                          }
                                          iconColor="primary"
                                          tooltipText={t(
                                            "contractTerms.table.reset"
                                          )}
                                          data-test={`revenue-item-row-reset-${item.id}`}
                                        >
                                          <Reactivate />
                                        </IconButton>
                                      </>
                                    ) : (
                                      <div
                                        className={cn(
                                          "flex items-center gap-1",
                                          !draft &&
                                            "opacity-0 group-hover:opacity-100 transition-opacity"
                                        )}
                                      >
                                        {draftStatus === "modified" &&
                                        draft?.onRevertItem ? (
                                          <IconButton
                                            variant="icon"
                                            size="md"
                                            onClick={() =>
                                              draft.onRevertItem?.(item.id)
                                            }
                                            iconColor="primary"
                                            tooltipText={t(
                                              "contractTerms.table.reset"
                                            )}
                                            data-test={`revenue-item-row-reset-${item.id}`}
                                          >
                                            <Reactivate />
                                          </IconButton>
                                        ) : null}
                                        {hideRowActions ? null : (
                                          <>
                                            <IconButton
                                              variant="icon"
                                              size="md"
                                              onClick={() =>
                                                handleEditRevenueItem(item)
                                              }
                                              iconColor="primary"
                                              disabled={disabled || isPending}
                                              tooltipText={
                                                disabled
                                                  ? disabledTooltipText
                                                  : isPending
                                                    ? t(
                                                        "contractTerms.table.saving"
                                                      )
                                                    : t(
                                                        "contractTerms.table.edit"
                                                      )
                                              }
                                              data-test={`revenue-item-row-edit-${item.id}`}
                                              // Opt this icon out of IconContainer's stroke normalization, which thins its diagonals.
                                              iconClassName="[&_path]:[vector-effect:none]"
                                            >
                                              <Edit />
                                            </IconButton>
                                            <IconButton
                                              variant="icon"
                                              size="md"
                                              // Kept destructive outside the draft
                                              // editor: there the delete is a
                                              // reversible mark, here it removes the
                                              // revenue item outright.
                                              destructive={!draft}
                                              onClick={() =>
                                                handleDeleteRevenueItem(item)
                                              }
                                              disabled={disabled || isPending}
                                              tooltipText={
                                                disabled
                                                  ? disabledTooltipText
                                                  : isPending
                                                    ? t(
                                                        "contractTerms.table.saving"
                                                      )
                                                    : t(
                                                        "contractTerms.table.delete"
                                                      )
                                              }
                                              data-test={`revenue-item-row-delete-${item.id}`}
                                            >
                                              <Delete />
                                            </IconButton>
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          }}
                        </Draggable>
                        {isExpanded
                          ? previousPeriods.map((period) => (
                              <tr
                                key={period.id}
                                className={cn(
                                  "border-b border-border-border1",
                                  inBundle && "bg-surface-recessed"
                                )}
                                data-test={`revenue-item-period-row-${period.id}`}
                              >
                                {showAddService ? <td /> : null}
                                <td />
                                <RevenueItemCells
                                  // Elapsed periods are historical — suppress
                                  // the current-row auto-renew marker even
                                  // though the underlying item still carries it.
                                  // A bundle line's period, like the line,
                                  // shows its total before the bundle discount.
                                  item={{
                                    ...(inBundle
                                      ? lineWithoutBundleDiscount(period)
                                      : period),
                                    autoRenewal: false
                                  }}
                                  {...serviceFlags}
                                  // An elapsed period carries its own dates and
                                  // duration, so its price divides by its own
                                  // length rather than the current period's.
                                  periodInMonths={recordedPeriodInMonths(
                                    period,
                                    getServiceItem(item)?.pricing?.find(
                                      (row) =>
                                        row.id === period.serviceItemPricingId
                                    )?.serviceItemPeriods,
                                    serviceFlags.isOneTime
                                  )}
                                  currency={currency}
                                  showConversion={showConversion}
                                  rate={rate}
                                  accountCurrencySymbol={accountCurrencySymbol}
                                  currencySignOverride={recordCurrencySignOverride}
                                  showStatusColumn={showStatusColumn}
                                  showNoteColumn={showNoteColumn}
                                />
                                <td />
                              </tr>
                            ))
                          : null}
                      </React.Fragment>
                    );
                  })}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
        {showAddService &&
          !hideAddService &&
          !showDraftEmptyState &&
          productId && (
            <AddServiceDropdown
              serviceOptions={serviceOptions}
              sections={sections}
              variant="tertiary"
              emptyStateContent={<ServiceEmptyState />}
              disabled={disabled}
              disabledTooltipText={disabledTooltipText}
              triggerClassName="w-full h-14 justify-start rounded-none px-5 data-[disabled=false]:hover:bg-surface-hover-soft"
              bundles={bundles}
              serviceItemTypes={serviceItemTypes}
              onSelectBundle={onSelectBundle}
            />
          )}
      </div>

      {showAddService && (
        <AddServiceDrawer
          isOpen={isDrawerOpen}
          fieldEditability={
            termsFieldSettings
              ? resolveLineEditability(
                  editingRevenueItem?.serviceItemId ?? selectedService?.id,
                  editingRevenueItem?.serviceItemPricingId
                )
              : null
          }
          lockServiceStartDate={lockServiceStartDate}
          lockBillingStartDate={lockBillingStartDate}
          allowServiceEndDateEdit={allowServiceEndDateEdit}
          defaultServiceStartDate={effectiveDate}
          onClose={handleCloseDrawer}
          selectedService={selectedService}
          contractId={contractId}
          onAddToContract={handleAddToContract}
          editingRevenueItem={editingRevenueItem}
          currency={rawCurrency}
          onLocalSubmit={
            draft
              ? (request, editingId) =>
                  editingId
                    ? draft.onUpdateItem(editingId, request)
                    : draft.onAddItem(request)
              : undefined
          }
        />
      )}
      <ConfirmationModal
        isOpen={!!pendingEditItem}
        setIsOpen={(open) => {
          if (!open) setPendingEditItem(null);
        }}
        title={t("contractTerms.signedConfirmation.updateTitle")}
        confirmationText={t("contractTerms.signedConfirmation.updateBody")}
        submitButtonText={t("contractTerms.signedConfirmation.updateConfirm")}
        isPrimaryButton
        handleSubmit={() => {
          if (pendingEditItem) openEditDrawer(pendingEditItem);
          setPendingEditItem(null);
        }}
        handleCancel={() => setPendingEditItem(null)}
        data-test="revenue-item-update-confirmation-modal"
        hideDivider
      />
      <ConfirmationModal
        isOpen={!!pendingDeleteItem}
        setIsOpen={(open) => {
          if (!open) setPendingDeleteItem(null);
        }}
        // A signed contract's warning comes first, even when the line is its
        // bundle's last.
        title={t(
          confirmServiceChanges
            ? "contractTerms.signedConfirmation.deleteTitle"
            : pendingDeleteItem && isLastBundleLine(pendingDeleteItem)
              ? "contractTerms.bundle.deleteTitle"
              : "contractTerms.deleteConfirmation.title"
        )}
        confirmationText={t(
          confirmServiceChanges
            ? "contractTerms.signedConfirmation.deleteBody"
            : pendingDeleteItem && isLastBundleLine(pendingDeleteItem)
              ? "contractTerms.bundle.deleteBody"
              : "contractTerms.deleteConfirmation.body"
        )}
        submitButtonText={t(
          confirmServiceChanges
            ? "contractTerms.signedConfirmation.deleteConfirm"
            : "contractTerms.deleteConfirmation.confirm"
        )}
        handleSubmit={() => {
          if (pendingDeleteItem) performDelete(pendingDeleteItem);
          setPendingDeleteItem(null);
        }}
        handleCancel={() => setPendingDeleteItem(null)}
        data-test="revenue-item-delete-confirmation-modal"
        hideDivider
      />
      <ConfirmationModal
        isOpen={!!pendingDeleteBundle}
        setIsOpen={(open) => {
          if (!open) setPendingDeleteBundle(null);
        }}
        title={t("contractTerms.bundle.deleteTitle")}
        confirmationText={t("contractTerms.bundle.deleteBody")}
        submitButtonText={t("contractTerms.deleteConfirmation.confirm")}
        handleSubmit={() => {
          if (pendingDeleteBundle) performBundleDelete(pendingDeleteBundle);
          // Deleting from the edit drawer closes it with the bundle.
          if (editingBundle?.bundle.id === pendingDeleteBundle?.id) {
            setEditingBundle(null);
          }
          setPendingDeleteBundle(null);
        }}
        handleCancel={() => setPendingDeleteBundle(null)}
        data-test="revenue-item-bundle-delete-confirmation-modal"
        hideDivider
      />
      {bundleDrawer}
      {editingBundle && editingBundleDetails && contractId ? (
        <DealBundleDrawer
          key={editingBundle.bundle.id}
          bundle={editingBundleDetails}
          services={availableServices}
          serviceItemTypes={serviceItemTypes}
          currency={rawCurrency}
          isSaving={updateContractBundleMutation.isPending}
          onClose={() => setEditingBundle(null)}
          edit={{
            lines: editingBundle.lines,
            servicesStartDate: editingBundle.bundle.servicesStartDate,
            onSave: (request, onDiscountRefused) =>
              updateContractBundleMutation.mutate(
                {
                  pathParams: { bundleId: editingBundle.bundle.id, contractId },
                  data: request
                },
                {
                  onSuccess: () => setEditingBundle(null),
                  onError: (error) =>
                    isBundleDiscountRefused(error)
                      ? onDiscountRefused()
                      : toast.error(t("contractTerms.bundle.updateError"))
                }
              ),
            onDelete: () => setPendingDeleteBundle(editingBundle.bundle)
          }}
        />
      ) : null}
    </>
  );
};

export default RevenueItemsTable;
